import {
	DateFieldConfiguration,
	EmailFieldConfiguration,
	FieldConfiguration,
	NumberFieldConfiguration,
	SelectFieldConfiguration,
	StringFieldConfiguration,
} from '../../../api/ConfigApi';
import { FieldValuesMap } from '../../../types/policy';
import { getFieldDetailsFromOntology } from '../../../util/util';
import {
	DATE_PRECISION,
	FIELD_TYPES,
	FORM_NAME,
	POLICYHOLDER_FIELD_TYPE,
} from '../data/constants';
import { FormBuilder, FormShape, PolicyholderTransformer } from '../types';

const getPolicyholderForm = (
	props: PolicyholderTransformer,
	name = FORM_NAME.policyholder as keyof FormShape<FormBuilder>,
) => {
	if (!props) {
		return {} as FormShape<FormBuilder>;
	}
	const { entity } = props;
	const { configData } = props;
	if (!configData || !entity) {
		return {} as FormShape<FormBuilder>;
	}
	const { policyholderCreateFields, ontologyData } = props.configData;

	const fields = [] as FieldConfiguration[];
	policyholderCreateFields &&
		policyholderCreateFields.forEach((section, sectionIndex) => {
			let heading = section.title;
			section.rows.forEach((row, rowIndex) => {
				row.properties.forEach((property, propertyIndex) => {
					const fieldDetails = getFieldDetailsFromOntology(
						ontologyData,
						property,
					);
					if (fieldDetails) {
						const newField = {} as
							| EmailFieldConfiguration
							| DateFieldConfiguration
							| NumberFieldConfiguration
							| SelectFieldConfiguration
							| StringFieldConfiguration;

						if (heading && propertyIndex === 0) {
							newField.heading = section.title;
							heading = null;
						}

						if (propertyIndex === 0) {
							newField.newline = true;
						}

						newField.width = 6;
						newField.title = property.title;
						newField.name = property.uri;
						newField.optional = !property.required;
						newField.ontologyType = fieldDetails.type.uri;
						if (property.visibleIf) {
							newField.condition = {
								// Visible-if conditions are named with a % where the tenantId typically is
								// To make this work with FastForm, we need to replace the % with the actual tenantId (with dashes removed)
								[property.visibleIf.uri.replace(
									'%',
									'_' + ontologyData.tenantId.replaceAll('-', ''),
								)]: property.visibleIf.values,
							};
						}
						switch (fieldDetails.type.uri) {
							case POLICYHOLDER_FIELD_TYPE.string:
								const stringField = newField as StringFieldConfiguration;
								newField.type = FIELD_TYPES.string;
								if (fieldDetails.type?.options?.regex) {
									stringField.regex = fieldDetails.type.options.regex;
								}
								if (fieldDetails.type?.options?.multiline) {
									stringField.multiline = true;
								}
								fields.push(stringField);
								break;
							case POLICYHOLDER_FIELD_TYPE.number:
								const numberField = newField as NumberFieldConfiguration;
								newField.type = FIELD_TYPES.number;
								fields.push(numberField);
								break;
							case POLICYHOLDER_FIELD_TYPE.date:
							case POLICYHOLDER_FIELD_TYPE.day:
								const dateField = newField as DateFieldConfiguration;
								dateField.type = FIELD_TYPES.date;
								dateField.precision =
									DATE_PRECISION.DAY as DateFieldConfiguration['precision'];
								fields.push(dateField);
								dateField.minimum = fieldDetails.type.options.minimumDateTime;
								dateField.maximum = fieldDetails.type.options.maximumDateTime;
								dateField.maximumOffset =
									fieldDetails.type.options.maximumOffset;
								dateField.minimumOffset =
									fieldDetails.type.options.minimumOffset;
								// Policyholder ontology data does not propagate the showCalendar flag properly, so we made a decision to leave it always on vs always off
								dateField.showCalendar = true;

								break;
							case POLICYHOLDER_FIELD_TYPE.select:
								const selectField = newField as SelectFieldConfiguration;
								selectField.type = FIELD_TYPES.select;
								selectField.values = fieldDetails.type.options.values;
								fields.push(selectField);
								break;
							case POLICYHOLDER_FIELD_TYPE.email:
								const emailField = newField as EmailFieldConfiguration;
								emailField.type = FIELD_TYPES.email;
								fields.push(emailField);
								break;
							default:
								break;
						}
					}
				});
			});
		});

	let defaultValues = {} as FieldValuesMap;

	if (entity && entity.values) {
		Object.keys(entity.values).forEach((key) => {
			defaultValues[key] = entity.values[key].value;
		});
	}

	return {
		[name]: {
			fields: fields,
			fieldGroupsByLocator: {},
			mediaByLocator: {},
			externalDataCalls: [],
			values: defaultValues,
		} as FormBuilder,
	} as FormShape<FormBuilder>;
};

export default getPolicyholderForm;
